import React from "react";
import { useNavigate } from "react-router-dom";
import { format } from "timeago.js";
import { useNotifications } from "../../hooks/useNotifications";
import { resolveNotificationNavigationPath } from "../../utils/notificationNavigation";
import { getNotificationRelatedOrderId } from "../../utils/orderNotificationMatch";
import { navigateWithMobileDashboardScroll } from "../../utils/navigateWithMobileDashboardScroll";

const OrderUpdatesPanel = ({
  notifications = [],
  emptyFallback = null,
  sellerView = false,
}) => {
  const navigate = useNavigate();
  const { markAsRead } = useNotifications();

  if (!Array.isArray(notifications) || notifications.length === 0) {
    return emptyFallback;
  }

  const handleOpen = async (notification) => {
    if (notification?._id && !notification.read) {
      try {
        await markAsRead(notification._id);
      } catch (error) {
        console.error("Error marking notification as read:", error);
      }
    }

    const path =
      resolveNotificationNavigationPath(notification) ||
      (() => {
        const orderId = getNotificationRelatedOrderId(notification);
        if (!orderId) return "";
        return sellerView
          ? `/profile/seller-order/${orderId}`
          : `/profile/order/${orderId}`;
      })();

    if (path) {
      navigateWithMobileDashboardScroll(navigate, path);
    }
  };

  return (
    <div
      className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-950"
      role="status"
    >
      <p className="font-semibold">
        {notifications.length === 1
          ? "1 order update"
          : `${notifications.length} order updates`}
      </p>
      <p className="mt-1 text-red-900/90">
        Open an update below to view the details — you do not need the notification bell.
      </p>
      <ul className="mt-3 flex flex-col gap-2">
        {notifications.slice(0, 12).map((notification) => {
          const orderId = getNotificationRelatedOrderId(notification);
          return (
            <li key={notification._id || `${notification.type}-${orderId}`}>
              <button
                type="button"
                onClick={() => handleOpen(notification)}
                className="w-full rounded-lg border border-red-200 bg-white px-3 py-2.5 text-left shadow-sm transition hover:border-red-300 hover:bg-red-50/80"
              >
                <span className="mb-1 inline-flex items-center gap-2">
                  <span className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                    New
                  </span>
                  <span className="text-sm font-semibold text-red-900">
                    {notification.title || "Order update"}
                  </span>
                </span>
                {notification.message ? (
                  <span className="mt-1 block text-xs leading-5 text-red-800/90">
                    {notification.message}
                  </span>
                ) : null}
                <span className="mt-1 block text-[11px] text-red-700/70">
                  {notification.createdAt ? format(notification.createdAt) : "Just now"}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default OrderUpdatesPanel;
